/**
 * Applied Superintelligence teaching launcher registry.
 * Live apps open. Coming-soon tiles show the AppLauncher chip and do not navigate.
 * Consulted only when isAsiMarketingHost() is true — never pollutes fusionmsi.
 */

import GavelRounded from "@mui/icons-material/GavelRounded";
import FlightRounded from "@mui/icons-material/FlightRounded";
import BugReportRounded from "@mui/icons-material/BugReportRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import BuildRounded from "@mui/icons-material/BuildRounded";
import PolicyRounded from "@mui/icons-material/PolicyRounded";
import {
  FACT_PATTERN_PROFILE,
  RADIO_CALL_PROFILE,
} from "./teachingAppProfiles";

/** @typedef {"live" | "comingSoon"} AsiTeachingAppStatus */

/**
 * @typedef {object} AsiTeachingAppEntry
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {AsiTeachingAppStatus} status
 * @property {string | null} to
 * @property {string | null} [href] Absolute URL for external apps (opens in a new tab)
 * @property {boolean} [adminOnly] When true, only global/regional admins see the tile
 * @property {boolean} comingSoon
 * @property {import("@mui/material").SvgIconComponent} icon
 */

/** Hosting placeholder until the owner creates the site (HANDOFF.md). */
export const COHERENTSPACE_EXTERNAL_URL =
  process.env.REACT_APP_COHERENTSPACE_URL || "https://coherentspace-md3c.web.app";

/** @type {readonly AsiTeachingAppEntry[]} */
export const ASI_TEACHING_APP_REGISTRY = Object.freeze([
  Object.freeze({
    id: FACT_PATTERN_PROFILE.id,
    name: FACT_PATTERN_PROFILE.displayName,
    description: "Listen to a fact pattern, then name the issues",
    status: "live",
    to: FACT_PATTERN_PROFILE.route,
    href: null,
    adminOnly: false,
    comingSoon: false,
    icon: GavelRounded,
  }),
  Object.freeze({
    id: RADIO_CALL_PROFILE.id,
    name: RADIO_CALL_PROFILE.displayName,
    description: "Listen to a radio call, then name the failures",
    status: "live",
    to: RADIO_CALL_PROFILE.route,
    href: null,
    adminOnly: false,
    comingSoon: false,
    icon: FlightRounded,
  }),
  Object.freeze({
    id: "coherentSpace",
    name: "CoherentSpace",
    description: "MD3C secure contract review",
    status: "live",
    to: null,
    href: COHERENTSPACE_EXTERNAL_URL,
    adminOnly: true,
    comingSoon: false,
    icon: PolicyRounded,
  }),
  Object.freeze({
    id: "pageBrief",
    name: "PageBrief",
    description: "On-call engineering handoff training",
    status: "comingSoon",
    to: null,
    href: null,
    adminOnly: false,
    comingSoon: true,
    icon: BugReportRounded,
  }),
  Object.freeze({
    id: "claimCall",
    name: "ClaimCall",
    description: "Claims, underwriting, and advice training",
    status: "comingSoon",
    to: null,
    href: null,
    adminOnly: false,
    comingSoon: true,
    icon: DescriptionRounded,
  }),
  Object.freeze({
    id: "faultCall",
    name: "FaultCall",
    description: "Field service and skilled trades training",
    status: "comingSoon",
    to: null,
    href: null,
    adminOnly: false,
    comingSoon: true,
    icon: BuildRounded,
  }),
]);

/**
 * Tiles shown on the ASI-host teaching launcher (live + coming soon).
 * @param {{ isAdmin?: boolean }} [options]
 * @returns {AsiTeachingAppEntry[]}
 */
export function getAsiTeachingLauncherApps(options = {}) {
  const isAdmin = options.isAdmin === true;
  return ASI_TEACHING_APP_REGISTRY.filter((app) => !app.adminOnly || isAdmin);
}

/**
 * Live ASI teaching apps only (have a route or external href).
 * @returns {AsiTeachingAppEntry[]}
 */
export function getLiveAsiTeachingApps() {
  return ASI_TEACHING_APP_REGISTRY.filter((app) => app.status === "live");
}

/**
 * @param {string} id
 * @returns {AsiTeachingAppEntry | undefined}
 */
export function getAsiTeachingAppById(id) {
  return ASI_TEACHING_APP_REGISTRY.find((app) => app.id === id);
}

/** True when the tile can be activated (in-app route or external href). */
export function isAsiTeachingAppOpenable(app) {
  if (!app || app.comingSoon) return false;
  return Boolean(app.to || app.href);
}
